import { Trans, t } from "@lingui/macro";
import React from "react";
import styled from "styled-components";
import { TimeOfDay } from "~/core/TimeOfDay";
import { Collapsible } from "~/guidelines/Collapsible";
import { Accessible, Clock, Close, WarningLine } from "~/guidelines/Icon";
import LabeledInformation from "~/guidelines/LabeledInformation";
import { ListItem, UnorderedList } from "~/guidelines/List";
import mediaQueries, { useIsMdScreen } from "~/guidelines/Theme/mediaQueries";
import { body1, heading3 } from "~/guidelines/Typography";
import { TimeFormat } from "~/localization";

const Container = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(7)};

  ${mediaQueries.md()} {
    max-width: none;
    padding: ${({ theme }) => theme.spacing(17, 0)};
  }
`;

const Title = styled.h3`
  flex-grow: 1;

  margin: 0;
  padding: 0;
  ${heading3};
  text-align: left;

  ${mediaQueries.md()} {
    /* padding-bottom: 72px; */
    text-align: center;
  }
`;

const Rules = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) => theme.spacing(3)};

  ${mediaQueries.md()} {
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing(10)};
    justify-content: center;
  }
`;

const Text = styled.p`
  margin: 0;
  padding: 0;
  ${body1};
`;

const StyledLabeledInformation = styled(LabeledInformation)`
  & dd {
    padding-left: 24px;
  }
`;

type ConditionsAndCancellationPolicyProps = {
  arrivalTime: TimeOfDay;
  departureTime: TimeOfDay;
  reducedMobilityRooms: boolean;
  elevator: boolean;
  stairliftPlatform: boolean;
};

const ConditionsAndCancellationPolicy = ({
  arrivalTime,
  departureTime,
  reducedMobilityRooms,
  elevator,
  stairliftPlatform,
}: ConditionsAndCancellationPolicyProps) => {
  const isMdScreen = useIsMdScreen();

  return (
    <Container>
      <Collapsible
        title={
          <Title>
            <Trans>Reservation and cancellation policy</Trans>
          </Title>
        }
        isDisabled={isMdScreen}
        isOpen={isMdScreen ? true : undefined}
      >
        <Rules>
          <div>
            <StyledLabeledInformation title={t`Check-in`} icon={<Clock />}>
              <UnorderedList>
                <ListItem>
                  <Trans>
                    Arrival is from{" "}
                    <TimeFormat
                      date={arrivalTime.asDate()}
                      format={{ hour: "2-digit", minute: "2-digit" }}
                    />
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans>
                    Departure is before{" "}
                    <TimeFormat
                      date={departureTime.asDate()}
                      format={{ hour: "2-digit", minute: "2-digit" }}
                    />
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans>
                    You can arrive earlier or leave later by taking options
                    during booking
                  </Trans>
                </ListItem>
              </UnorderedList>
            </StyledLabeledInformation>

            <StyledLabeledInformation
              title={t`House rules`}
              icon={<WarningLine />}
            >
              <UnorderedList>
                <ListItem>
                  <Trans>No smoking inside but allowed on the terrace</Trans>
                </ListItem>
                <ListItem>
                  <Trans>Pets are not allowed</Trans>
                </ListItem>
                <ListItem>
                  <Trans>No parties (even if you are really quiet)</Trans>
                </ListItem>
                <ListItem>
                  <Trans>
                    No outside visitors are allowed, even in our common areas
                  </Trans>
                </ListItem>
              </UnorderedList>
            </StyledLabeledInformation>
          </div>

          <div>
            <StyledLabeledInformation
              title={t`Flexible cancellation`}
              icon={<Close />}
            >
              <Text>
                <Trans>
                  You are fully refunded if you cancel before 6pm on the day of
                  arrival.
                </Trans>
              </Text>
            </StyledLabeledInformation>

            <StyledLabeledInformation
              title={t`Accessibility`}
              icon={<Accessible />}
            >
              <UnorderedList>
                {reducedMobilityRooms ? (
                  <ListItem>
                    <Trans>PRM room</Trans>
                  </ListItem>
                ) : null}
                {stairliftPlatform ? (
                  <ListItem>
                    <Trans>The Loge offers a platform lift</Trans>
                  </ListItem>
                ) : null}
                {elevator ? (
                  <ListItem>
                    <Trans>There is lift in the house</Trans>
                  </ListItem>
                ) : (
                  <ListItem>
                    <Trans>There is no lift in the house</Trans>
                  </ListItem>
                )}
              </UnorderedList>
            </StyledLabeledInformation>
          </div>
        </Rules>
      </Collapsible>
    </Container>
  );
};

export default ConditionsAndCancellationPolicy;
